import { useQuery } from '@tanstack/react-query';

import { getSelectedFacebookPredictedLtv } from '@features/Analytics/services/facebook-ltv';
import { getStripeProductId } from '@features/Stripe/api';
import type { TpStripeVariants } from 'src/constants/variants';
import { getDataFromSessionStorage } from 'src/utils';

type ProductData = {
  pltv?: null | string;
  product_id: string;
  us_pltv?: null | string;
  us_product_id?: string;
};

export const useGetStripeProductId = ({
  variant,
  paymentSystemRegion,
  enabled = true,
}: {
  variant: TpStripeVariants;
  paymentSystemRegion?: string;
  enabled?: boolean;
}) => {
  const userCountry = getDataFromSessionStorage({
    store: 'quizStore',
    key: 'countryCode',
  });

  const { data } = useQuery({
    queryKey: ['stripe-product-id', variant, userCountry],
    queryFn: () => getStripeProductId({ variant }),
    enabled: !!variant && enabled,
    gcTime: 20 * 60 * 1000, // 20 minutes
    staleTime: 20 * 60 * 1000, // Consider data fresh for 20 minutes
    retry: 0, // No retries on failure
    select: (data: ProductData) => {
      const isUsProductIdValid = Boolean(
        data?.us_product_id && data.us_product_id.length > 1,
      );

      const isUsFeatureEnabled =
        paymentSystemRegion === 'FaceYogaUS' ||
        (!paymentSystemRegion && userCountry === 'US' && isUsProductIdValid);

      const predictedLtv = getSelectedFacebookPredictedLtv({
        isUsFeatureEnabled,
        pltv: data?.pltv,
        usPltv: data?.us_pltv,
      });

      if (paymentSystemRegion && paymentSystemRegion === 'FaceYogaUS') {
        return {
          productId: data?.us_product_id,
          isUsProductIdValid: isUsProductIdValid,
          isUsFeatureEnabled,
          pltv: data?.pltv ?? '',
          predictedLtv,
          usPltv: data?.us_pltv ?? '',
        };
      } else if (paymentSystemRegion && paymentSystemRegion === 'FaceYoga') {
        return {
          productId: data?.product_id,
          isUsProductIdValid: isUsProductIdValid,
          isUsFeatureEnabled,
          pltv: data?.pltv ?? '',
          predictedLtv,
          usPltv: data?.us_pltv ?? '',
        };
      } else {
        return {
          productId:
            userCountry === 'US' && isUsProductIdValid
              ? data?.us_product_id
              : data?.product_id,
          isUsProductIdValid: isUsProductIdValid,
          isUsFeatureEnabled,
          pltv: data?.pltv ?? '',
          predictedLtv,
          usPltv: data?.us_pltv ?? '',
        };
      }
    },
  });

  return (
    data ?? {
      productId: undefined,
      isUsProductIdValid: false,
      isUsFeatureEnabled: false,
      pltv: '',
      predictedLtv: undefined,
      usPltv: '',
    }
  );
};
